export type ResultMetric = { key: string; label: string; count: number };
export type ResultEntityGroup = { key: string; label: string; items: string[]; more: number };
export type ResultStep = { n: string; text: string };
export type ResultFinding = { title: string; body: string };

export type MaestroResultModel = {
  kicker: string;
  title?: string;
  lead?: string;
  metrics: ResultMetric[];
  groups: ResultEntityGroup[];
  findings: ResultFinding[];
  steps: ResultStep[];
  remainder: string;
  kind: 'inventory' | 'diagnosis' | 'answer';
};

function keyOf(label: string): string {
  return label.toLowerCase().replace(/s\b/g, '').replace(/[^a-z0-9]/g, '');
}

function titleCase(label: string): string {
  const raw = label.trim();
  if (!raw) return raw;
  if (raw === raw.toUpperCase()) return raw;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function stripDecor(line: string): string {
  return line.replace(/\*\*/g, '').replace(/^[-•*]\s+/, '').trim();
}

function parseInventoryLine(line: string): { label: string; count: number; items: string[]; more: number } | null {
  const t = stripDecor(line);
  const match = t.match(
    /^([A-Za-z][A-Za-z0-9 _/-]{0,40}?)\s*:\s*(\d+)\s*(?:record\(s\))?\s*(?:[—–-]\s*)?(?:e\.g\.\s*)?(.*)$/i,
  );
  if (!match) return null;
  const label = match[1].trim();
  if (!label || /master data|inventory|dimension studio/i.test(label)) return null;
  const count = Number(match[2]);
  let rest = (match[3] || '').trim();
  let more = 0;
  const moreMatch = rest.match(/\(\+(\d+)\s*more\)\s*$/i);
  if (moreMatch) {
    more = Number(moreMatch[1]);
    rest = rest.slice(0, moreMatch.index).replace(/[—–-]\s*$/, '').trim();
  }
  const items = rest
    ? rest.split(/,\s*/).map((item) => item.trim()).filter(Boolean)
    : [];
  return { label, count, items, more };
}

function displayLabel(label: string, count: number): string {
  const base = titleCase(label.replace(/s$/i, ''));
  return count === 1 ? base : `${base}s`;
}

function mergeGroup(
  existing: ResultEntityGroup | undefined,
  next: { label: string; count: number; items: string[]; more: number },
): ResultEntityGroup {
  const items = [...(existing?.items || [])];
  for (const item of next.items) {
    if (!items.includes(item)) items.push(item);
  }
  const total = Math.max(existing ? existing.items.length + existing.more : 0, next.count, items.length + next.more);
  const visible = items.slice(0, 8);
  return {
    key: keyOf(next.label),
    label: displayLabel(next.label, total),
    items: visible,
    more: Math.max(0, total - visible.length),
  };
}

export function parseMaestroResult(raw: string): MaestroResultModel {
  const text = String(raw || '').replace(/\r\n/g, '\n').trim();
  const lines = text.split('\n');
  const metricsMap = new Map<string, ResultEntityGroup>();
  const findings: ResultFinding[] = [];
  const steps: ResultStep[] = [];
  const leftover: string[] = [];
  let kicker = 'MAESTRO';
  let title: string | undefined;
  let lead: string | undefined;
  let section: 'none' | 'do' | 'found' = 'none';
  let inventoryHit = false;

  const flushFinding = (heading: string, body: string) => {
    const trimmed = body.trim();
    if (!heading && !trimmed) return;
    findings.push({ title: heading.replace(/:$/, ''), body: trimmed.replace(/\*\*/g, '') });
  };

  let pendingFindingTitle = '';
  let pendingFindingBody: string[] = [];

  const commitFinding = () => {
    if (pendingFindingTitle || pendingFindingBody.length) {
      flushFinding(pendingFindingTitle, pendingFindingBody.join(' ').trim());
      pendingFindingTitle = '';
      pendingFindingBody = [];
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      section = section === 'found' ? 'none' : section;
      continue;
    }

    if (/^(from master data|master data inventory|here's your master data)/i.test(stripDecor(line))) {
      inventoryHit = true;
      kicker = 'MASTER DATA';
      title = title || 'Workspace inventory';
      section = 'none';
      continue;
    }

    const heading = line.match(/^(?:#{1,4}\s+|\*\*)([^*#]+?)(?:\*\*)?\s*:?\s*$/);
    const headingInline = line.match(/^\*\*([^*]+?)\*\*:?\s+(\S.*)$/);
    if (headingInline) {
      commitFinding();
      const name = headingInline[1].replace(/:$/, '').trim();
      if (/what to do|next step/i.test(name)) {
        section = 'do';
        continue;
      }
      if (/what i found|finding/i.test(name)) {
        section = 'found';
        pendingFindingTitle = name;
        pendingFindingBody = [headingInline[2]];
        continue;
      }
      leftover.push(line);
      continue;
    }
    if (heading && !parseInventoryLine(line)) {
      const name = heading[1].trim();
      if (/what to do|next step/i.test(name)) {
        commitFinding();
        section = 'do';
        continue;
      }
      if (/what i found|finding/i.test(name)) {
        commitFinding();
        section = 'found';
        pendingFindingTitle = name;
        continue;
      }
    }

    const inventory = parseInventoryLine(line);
    if (inventory && (inventoryHit || /^[-•]/.test(line) || /^\*\*/.test(line))) {
      inventoryHit = true;
      kicker = 'MASTER DATA';
      title = title || 'Workspace inventory';
      const key = keyOf(inventory.label);
      metricsMap.set(key, mergeGroup(metricsMap.get(key), inventory));
      continue;
    }

    const step = line.match(/^(\d+)[.)]\s+(.*)$/);
    if (step && (section === 'do' || steps.length > 0 || inventoryHit === false)) {
      if (section === 'do' || steps.length > 0) {
        commitFinding();
        steps.push({ n: String(Number(step[1])).padStart(2, '0'), text: step[2].replace(/\*\*/g, '').trim() });
        continue;
      }
    }

    if (section === 'found') {
      pendingFindingBody.push(stripDecor(line));
      continue;
    }

    leftover.push(line);
  }
  commitFinding();

  const groups = [...metricsMap.values()];
  const metrics: ResultMetric[] = groups.map((group) => ({
    key: group.key,
    label: group.label,
    count: group.items.length + group.more || group.items.length,
  }));
  for (const group of groups) {
    const total = group.items.length + group.more;
    const metric = metrics.find((row) => row.key === group.key);
    if (metric) metric.count = Math.max(metric.count, total);
  }

  const remainderLines: string[] = [];
  for (const line of leftover) {
    const plain = stripDecor(line);
    if (!plain) continue;
    if (!lead && plain.length > 28 && !/Data Dimension Studio|master_data_records/i.test(plain)) {
      lead = plain.replace(/\*\*/g, '');
      continue;
    }
    remainderLines.push(line);
  }

  let kind: MaestroResultModel['kind'] = 'answer';
  if (groups.length) kind = 'inventory';
  else if (steps.length || findings.length) kind = 'diagnosis';

  if (kind === 'diagnosis') kicker = kicker === 'MAESTRO' ? 'SELF-CHECK' : kicker;

  return {
    kicker,
    title,
    lead,
    metrics,
    groups,
    findings,
    steps,
    remainder: remainderLines.join('\n').trim(),
    kind,
  };
}
